import {
  CFormType,
  type FormFieldGroup,
  type FormFragment,
} from '@seek/cmsu-cms-connect';
import type { Country, SiteName } from '@seek/melways-sites';
import {
  isEmail,
  maxLength,
  maxValue,
  minLength,
  minValue,
} from '@seek/validators-js';
import React from 'react';
import { renderEmail } from 'react-html-email';

import { EmailContentComponent } from './EmailContentComponent';
import type { DynamicFormValues, FormField, ValidatorOptions } from './types';

type FormValidator =
  | {
      getErrorMessage: () => Promise<string>;
      test: ({ value }: { value: string }) => boolean;
    }
  | {
      getErrorMessage: () => Promise<string>;
      test: ({ value }: { value: number }) => boolean;
    }
  | {
      getErrorMessage: () => Promise<string>;
      test: ({ value }: { value: object }) => boolean;
    };

export const transformValueToValidator = (
  validator: ValidatorOptions,
  t: any,
): FormValidator => {
  switch (validator.__typename) {
    case 'MinLengthValidator':
      return {
        getErrorMessage: () =>
          Promise.resolve(
            validator.errorMessage ||
              t('Min length', { value: validator.value }),
          ),
        test: ({ value }: { value: string }) =>
          minLength.test({
            value,
            validatorProps: { min: validator.value },
            formValues: undefined,
          }),
      };
    case 'MaxLengthValidator':
      return {
        getErrorMessage: () =>
          Promise.resolve(
            validator.errorMessage ||
              t('Max length', { value: validator.value }),
          ),
        test: ({ value }: { value: string }) =>
          maxLength.test({
            value,
            validatorProps: { max: validator.value },
            formValues: undefined,
          }),
      };
    case 'MinNumberValidator':
      return {
        getErrorMessage: () =>
          Promise.resolve(
            validator.errorMessage ||
              t('Min number', { value: validator.value }),
          ),
        test: ({ value }: { value: number }) =>
          minValue.test({
            value,
            validatorProps: {
              min: validator.value,
              minValueFieldLabel: '',
            },
            formValues: undefined,
          }),
      };
    case 'MaxNumberValidator':
      return {
        getErrorMessage: () =>
          Promise.resolve(
            validator.errorMessage ||
              t('Max number', { value: validator.value }),
          ),
        test: ({ value }: { value: number }) =>
          maxValue.test({
            value,
            validatorProps: {
              max: validator.value,
              maxValueFieldLabel: '',
            },
            formValues: undefined,
          }),
      };
    case 'EmailValidator':
      return {
        getErrorMessage: () =>
          Promise.resolve(validator.errorMessage || t('Invalid email')),
        test: ({ value }: { value: string }) =>
          isEmail.test({
            value,
            formValues: undefined,
            validatorProps: undefined,
          }),
      };
    case 'MinCheckedItemValidator':
      return {
        getErrorMessage: () =>
          Promise.resolve(
            validator.errorMessage ||
              t('Min checked item', { value: validator.value }),
          ),
        test: ({ value: obj }: { value: object }) => {
          const checkedCount = Object.values(obj).filter(
            (value) => value === true,
          ).length;
          return checkedCount >= validator.value;
        },
      };
    case 'MaxCheckedItemValidator':
      return {
        getErrorMessage: () =>
          Promise.resolve(
            validator.errorMessage ||
              t('Max checked item', { value: validator.value }),
          ),
        test: ({ value: obj }: { value: object }) => {
          const checkedCount = Object.values(obj).filter(
            (value) => value === true,
          ).length;
          return checkedCount <= validator.value;
        },
      };
  }
};

/**
 * Prepares form data and URL based on form type for API submission
 */
export const prepareFormData = (
  form: FormFragment,
  formValues: DynamicFormValues,
  wptSalesForceApi: string,
  country: Country,
  site: SiteName,
  sourceName: string,
  recaptchaValue: string,
) => {
  let data: any;
  let url: string;

  switch (form.formType) {
    case CFormType.SaraEnquiry:
      data = {
        from: 'saras@seek.com.au',
        to: ['saras@seek.com.au'],
        subject: 'SARAs Enquiry',
        message: renderEmail(
          <EmailContentComponent
            name={formValues.name}
            company={formValues.company}
            email={formValues.email}
            enquiry={formValues.enquiry}
            subject="SARAs Enquiry"
          />,
        ),
        recaptcha: recaptchaValue,
      };
      url = `${wptSalesForceApi}/sara`;
      break;
    case CFormType.MailChimp:
      data = {
        ...formValues,
        apiEndpoint: form.apiEndpoint,
        SITECNTRY: country.toUpperCase(),
        recaptcha: recaptchaValue,
      };
      url = `${wptSalesForceApi}/mailChimp-subscription`;
      break;
    case CFormType.HirerSubscription:
      data = {
        ...formValues,
        dateSubscribed: new Date().toISOString(),
        sourceName,
        siteKey: country.toUpperCase(),
      };
      url = `${wptSalesForceApi}/hirer-subscription`;
      break;

    case CFormType.ContactUs:
      data = {
        ...formValues,
        formType: form.formType,
        isEmployer: site.includes('employer'),
        siteCountry: country.toUpperCase(),
        sourcePage: sourceName,
        recaptcha: recaptchaValue,
      };
      url = `${wptSalesForceApi}/contact`;
      break;
    default:
      data = {
        ...formValues,
        siteCountry: country.toUpperCase(),
        employerJobSeeker: site.includes('employer')
          ? 'Employer'
          : 'Job Seeker',
        sourcePage: sourceName,
        recaptcha: recaptchaValue,
        ...(form.apiEndpoint === 'hirer-subscription' && {
          dateSubscribed: new Date().toISOString(),
          sourceName,
          siteKey: country.toUpperCase(),
        }),
      };
      url = `${wptSalesForceApi}/${form.apiEndpoint}`;
  }

  return { data, url };
};

/**
 * Filters form fields to separate hidden and non-hidden field groups
 */
export const filterFormFields = (fields: FormField[]) => {
  const nonHiddenFields = fields.filter(
    (field) => field.__typename !== 'FormHiddenFieldGroup',
  );

  const hiddenFieldGroups = fields.filter(
    (field) => field.__typename === 'FormHiddenFieldGroup',
  );

  return { nonHiddenFields, hiddenFieldGroups };
};

/**
 * Filters nested fields within a FormFieldGroup to exclude hidden fields
 */
export const filterVisibleNestedFields = (fieldGroup: FormFieldGroup) =>
  fieldGroup.fields?.filter(
    (nestedField) => nestedField.__typename !== 'FormHiddenFieldGroup',
  ) || [];

/**
 * Custom hook to manage form field filtering
 */
export const useFormFieldFiltering = (fields: FormField[]) =>
  React.useMemo(() => filterFormFields(fields), [fields]);
